/**
 * Pure logic behind the TopBar "חזרה" button (`useBackNavigation`). No React, no DOM,
 * so the unit tests cover it.
 *
 * The router stamps every history entry of this tab with an index
 * (`history.state.idx`). The app writes down what it showed at each index (the
 * "trail"). Back then walks the trail backwards to the last entry that was a
 * DIFFERENT page (tab, filter and section changes on the same page are pushes too,
 * and "the previous page" skips them) and goes there with `navigate(-n)`, so that
 * page's own query (`?tab=`, filters) comes back exactly as it was. When there is
 * no such entry in this tab (a direct link, a new tab, the first page after
 * login), Back goes "up" instead: to the page's parent list, or home.
 */

export interface TrailEntry {
  pathname: string;
  search: string;
}

/** What the app showed at each history index of this tab. */
export type Trail = Record<number, TrailEntry>;

/** How far back the trail is kept and searched (entries). */
export const TRAIL_SPAN = 100;

const normalize = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

/** The home page (both of its views, `/` and `/?tab=dashboard`): no Back button there. */
export function isHomePath(pathname: string): boolean {
  return normalize(pathname) === '/';
}

/** Pages outside the staff shell (login, the customer portal) are never "the previous page". */
export function isShellPath(pathname: string): boolean {
  const p = normalize(pathname);
  return !['/login', '/portal'].some((root) => p === root || p.startsWith(`${root}/`));
}

/** Lists whose items open at `/<list>/:id`. */
const LISTS = new Set(['tickets', 'tasks', 'projects', 'milestones', 'organizations', 'leads', 'quotes', 'engagements', 'integrations']);

/**
 * Pages below the sidebar's level with no nav entry of their own: "ללוח השנה
 * המלא" from Home's agenda (its own back link moved to the TopBar on 2026-10-07).
 */
const SUB_PAGES = new Set(['/workspace/calendar']);

/**
 * Where the TopBar shows "חזרה" (round 2, 2026-10-11): detail pages
 * (`/tickets/:id`, `/projects/:id`, … at any depth below a list) and the sub-pages
 * above. Main pages (home, lists, settings sections), which the sidebar reaches
 * directly, show none.
 */
export function isDetailPath(pathname: string): boolean {
  const p = normalize(pathname);
  const segs = p.split('/').filter(Boolean);
  return (segs.length >= 2 && LISTS.has(segs[0])) || SUB_PAGES.has(p);
}

/**
 * The page one level up: `/tickets/:id` → `/tickets`, `/projects/:id` → `/projects`, …;
 * every other page (a list, settings, the calendar) → home.
 */
export function parentPath(pathname: string): string {
  const segs = normalize(pathname).split('/').filter(Boolean);
  if (segs.length >= 2 && LISTS.has(segs[0])) return `/${segs[0]}`;
  return '/';
}

/** A same-origin app path from `?from=`, or null (absolute URLs, `//host`, login, portal). */
export function safeFrom(search: string, pathname: string): string | null {
  const from = new URLSearchParams(search).get('from');
  if (!from || !from.startsWith('/') || from.startsWith('//') || from.includes('\\')) return null;
  const fromPath = from.split(/[?#]/)[0];
  if (!isShellPath(fromPath) || normalize(fromPath) === normalize(pathname)) return null;
  return from;
}

/**
 * Where Back goes when this tab has no earlier page: the page that linked here
 * (`?from=`), else what the page says its parent is (a project task → `/tasks`,
 * a milestone → its project), else the route's parent list, else home.
 */
export function fallbackTarget(pathname: string, search: string, pageParent?: string | null): string {
  return safeFrom(search, pathname) ?? (pageParent || parentPath(pathname));
}

/** Write down what index `idx` shows. A push drops the (now unreachable) forward entries. */
export function recordEntry(trail: Trail, idx: number, entry: TrailEntry, push: boolean): Trail {
  const next: Trail = {};
  for (const [k, v] of Object.entries(trail)) {
    const i = Number(k);
    if (i < idx - TRAIL_SPAN) continue;
    if (push && i > idx) continue;
    next[i] = v;
  }
  next[idx] = { pathname: entry.pathname, search: entry.search };
  return next;
}

/**
 * The history step back to the previous page (a negative number for `navigate`),
 * or null when there is none in this tab: Back then goes to `fallbackTarget`.
 * Entries of the current page (tabs, filters, sections) are skipped; an unknown
 * entry, or one outside the staff shell, stops the walk.
 */
export function backStep(trail: Trail, idx: number, current: Pick<TrailEntry, 'pathname'>): number | null {
  const here = normalize(current.pathname);
  for (let j = idx - 1; j >= 0 && idx - j <= TRAIL_SPAN; j -= 1) {
    const e = trail[j];
    if (!e || !isShellPath(e.pathname)) return null;
    if (normalize(e.pathname) !== here) return j - idx;
  }
  return null;
}

/** Parse a stored trail; anything malformed reads as empty. */
export function parseTrail(raw: string | null): Trail {
  if (!raw) return {};
  try {
    const data: unknown = JSON.parse(raw);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
    const out: Trail = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      const i = Number(k);
      const e = v as Partial<TrailEntry> | null;
      if (Number.isInteger(i) && i >= 0 && e && typeof e.pathname === 'string' && typeof e.search === 'string') {
        out[i] = { pathname: e.pathname, search: e.search };
      }
    }
    return out;
  } catch {
    return {};
  }
}
